import { create } from "zustand";
import type { SheetOutline } from "Worker/entities";
import type { CanvasPoint } from "components/canvas/canvasPoint";
import type { SyncSource } from "components/canvas/leftSide/import/GoogleSheets/spreadsheetApi";

/** A Google spreadsheet read through a saved connection. */
export interface GoogleSpreadsheet {
  savedConnectionId: string;
  spreadsheetId: string;
  spreadsheetName: string;
  /** Rows and columns per tab, so each is read from A1. */
  sizes: Record<string, [number, number]>;
  /** Per tab, the Google Tables a table can sync from. */
  syncSources?: Record<string, SyncSource[]>;
}

/** A workbook to draw tables on: a dropped file, or a Google spreadsheet. */
export type PendingWorkbook = {
  /** The queue row that waits for this choice; it goes once tables are picked. */
  rowId: number;
  modelId: string;
  sheets: SheetOutline[];
  /** Where the workbook was dropped; its tables land there, side by side. */
  at?: CanvasPoint;
} & (
  | { file: File; spreadsheet?: undefined }
  | { file?: undefined; spreadsheet: GoogleSpreadsheet }
);

/** Ids for pickers opened without a queue row; negative so they never meet one. */
let nextSpreadsheetRow = -1;
export const spreadsheetRowId = () => nextSpreadsheetRow--;

/** Workbooks waiting for the user to pick their tables, shown one at a time. */
export const useWorkbookImportStore = create<{ queue: PendingWorkbook[] }>(
  () => ({ queue: [] }),
);

export function openWorkbook(workbook: PendingWorkbook): void {
  const id = workbook.spreadsheet?.spreadsheetId;
  useWorkbookImportStore.setState(({ queue }) =>
    queue.some(
      (w) =>
        w.rowId === workbook.rowId ||
        (id !== undefined && w.spreadsheet?.spreadsheetId === id),
    )
      ? { queue }
      : { queue: [...queue, workbook] },
  );
}

export function closeWorkbook(): void {
  useWorkbookImportStore.setState(({ queue }) => ({ queue: queue.slice(1) }));
}
